import React, { useState, useEffect } from 'react';
import { Star, Search } from 'lucide-react';
import { locationReviewService } from '../../../services/locationReviewService';

export default function LocationReviewsSection() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 6;

  useEffect(() => {
    loadReviews();
  }, [currentPage, searchQuery]);

  const loadReviews = async () => {
    setLoading(true);
    setError('');
    try {
      const { data, count, error: fetchError } = await locationReviewService?.getAll(
        currentPage,
        pageSize,
        searchQuery
      );
      
      if (fetchError) throw fetchError;
      
      setReviews(data);
      setTotalCount(count);
    } catch (err) {
      console.error('Error loading location reviews:', err);
      setError(err?.message || 'Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e?.preventDefault();
    setCurrentPage(1); // Reset to first page on new search
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex gap-1">
        {[...Array(5)]?.map((_, index) => (
          <Star
            key={index}
            className={`w-5 h-5 ${index < rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
          />
        ))}
      </div>
    );
  };

  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h3 
            className="text-4xl font-black text-gray-900 mb-4"
            style={{ fontFamily: 'Roboto Slab, serif' }}
          >
            Reviews from Real Clients
          </h3>
          <p 
            className="text-xl text-gray-600"
            style={{ fontFamily: 'Open Sans, sans-serif' }}
          >
            Real experiences from families we have helped across California
          </p>
        </div>

        {/* Search Bar */}
        <div className="mb-8">
          <form onSubmit={handleSearch} className="max-w-2xl mx-auto">
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  placeholder="Search reviews by name, location, or content..."
                  className="w-full pl-10 pr-4 py-3 border-2 border-[#007BFF]/20 rounded-xl focus:ring-2 focus:ring-[#007BFF] focus:border-transparent transition-all"
                  style={{ fontFamily: 'Open Sans, sans-serif' }}
                />
              </div>
              <button
                type="submit"
                className="px-8 py-3 bg-[#007BFF] text-white rounded-xl font-bold hover:bg-blue-700 transition-all transform hover:scale-105 shadow-lg"
                style={{ fontFamily: 'Roboto Slab, serif' }}
              >
                Search
              </button>
            </div>
          </form>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-xl">
            <p className="text-red-800 font-semibold">{error}</p>
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-[#007BFF] border-t-transparent"></div>
          </div>
        ) : (
          <>
            {/* Reviews Grid */}
            {reviews?.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-600 text-xl" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                  No reviews found matching your search.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8">
                {reviews?.map((review) => (
                  <div
                    key={review?.id}
                    className="bg-white border-2 border-[#007BFF]/20 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h4 
                          className="text-xl font-black text-gray-900 mb-2"
                          style={{ fontFamily: 'Roboto Slab, serif' }}
                        >
                          {review?.clientName}
                        </h4>
                        <div className="mb-3">{renderStars(review?.rating)}</div>
                      </div>
                    </div>

                    {review?.location && (
                      <div className="inline-flex items-center bg-gradient-to-r from-[#007BFF] to-blue-600 text-white px-4 py-2 rounded-full mb-4">
                        <span className="text-sm font-bold">{review?.location}</span>
                      </div>
                    )}

                    <p 
                      className="text-base text-gray-700 mb-4 leading-relaxed"
                      style={{ fontFamily: 'Open Sans, sans-serif' }}
                    >
                      {review?.reviewText}
                    </p>

                    <div className="mt-4 text-sm text-gray-500" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                      {new Date(review?.createdAt)?.toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-[#F8F9FA] to-blue-50 border-2 border-[#007BFF]/20 rounded-2xl shadow-lg">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={handlePrevPage}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-6 py-3 border-2 border-[#007BFF] text-base font-bold rounded-xl text-[#007BFF] bg-white hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    style={{ fontFamily: 'Roboto Slab, serif' }}
                  >
                    Previous
                  </button>
                  <button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    className="ml-3 relative inline-flex items-center px-6 py-3 border-2 border-[#007BFF] text-base font-bold rounded-xl text-[#007BFF] bg-white hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    style={{ fontFamily: 'Roboto Slab, serif' }}
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-base text-gray-700 font-semibold" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                      Showing{' '}
                      <span className="font-black text-[#007BFF]">{(currentPage - 1) * pageSize + 1}</span> to{' '}
                      <span className="font-black text-[#007BFF]">
                        {Math.min(currentPage * pageSize, totalCount)}
                      </span>{' '}
                      of <span className="font-black text-[#007BFF]">{totalCount}</span> reviews
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-xl shadow-lg -space-x-px">
                      <button
                        onClick={handlePrevPage}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-3 py-3 rounded-l-xl border-2 border-[#007BFF] bg-white text-base font-bold text-[#007BFF] hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                      >
                        <span className="sr-only">Previous</span>
                        <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                      {[...Array(totalPages)]?.map((_, index) => {
                        const pageNum = index + 1;
                        if (
                          pageNum === 1 ||
                          pageNum === totalPages ||
                          (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                        ) {
                          return (
                            <button
                              key={pageNum}
                              onClick={() => setCurrentPage(pageNum)}
                              className={`relative inline-flex items-center px-5 py-3 border-2 text-base font-bold transition-all ${
                                currentPage === pageNum
                                  ? 'z-10 bg-[#007BFF] border-[#007BFF] text-white'
                                  : 'bg-white border-[#007BFF] text-[#007BFF] hover:bg-blue-50'
                              }`}
                              style={{ fontFamily: 'Roboto Slab, serif' }}
                            >
                              {pageNum}
                            </button>
                          );
                        } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                          return (
                            <span
                              key={pageNum}
                              className="relative inline-flex items-center px-5 py-3 border-2 border-[#007BFF] bg-white text-base font-bold text-gray-700"
                            >
                              ...
                            </span>
                          );
                        }
                        return null;
                      })}
                      <button
                        onClick={handleNextPage}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-3 py-3 rounded-r-xl border-2 border-[#007BFF] bg-white text-base font-bold text-[#007BFF] hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                      >
                        <span className="sr-only">Next</span>
                        <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}